const homeContent = {
  'en-US': {
    seo: {
      title: 'Lean it 101 | We make companies good at building software',
      description:
        'Less money burned on software that never ships. Strategy with your executives, senior engineers working with AI agents, and results landing in production week by week.',
    },
    hero: {
      eyebrow: 'Lean it 101 · Strategy · Engineering · AI',
      headline: 'We make companies',
      headlineAccent: 'good at building software.',
      body: 'Less money burned on software that never ships. We rebuild how you plan, build and deliver, and you watch the results land in production, week by week.',
      reassurance: 'Start with one workflow. We reply within one business day.',
      speed: [
        'Assessment in 1 to 2 weeks',
        'First production delivery in 2 to 4 weeks',
      ],
      primaryCta: 'See what we do',
      secondaryCta: 'See how we work',
      orbit: {
        aria: 'Your operation at the centre, with senior teams orbiting it, inside a continuous cycle: discover, design, build, validate, deliver and handover.',
        center: 'YOUR OPERATION',
        items: ['Support · Billing', 'Onboarding · Data', 'Handoffs · Reporting'],
        pod: 'TEAM',
        stages: ['Discover', 'Design', 'Build', 'Validate', 'Deliver', 'Handover'],
        cap1: 'SENIOR ENGINEERS AND AI AGENTS, INSIDE YOUR OPERATION',
        cap2: 'FROM BUSINESS PAIN TO WORKING SOFTWARE IN PRODUCTION',
      },
    },
    paths: {
      lead: 'Ways to work with us',
      connective:
        'Every path runs on the same operating model. Transformation help is optional: bring us in when you want the organization redesigned around the system.',
      maestro: {
        label: 'Maestro · the delivery operating system',
        headline: 'Run the system.',
        body: 'Adopt Maestro and run your own delivery on it: humans and AI agents in one system, forecasts computed from your own throughput, dates you can take to the board.',
        points: [
          'Visibility, control and predictability from the first week',
          'Works with the coding agents you already use',
          'No consulting engagement required',
        ],
        cta: 'Explore Maestro',
      },
    },
    build: {
      label: 'Forward Deployed Engineering',
      headline: 'Bring us one workflow.',
      body: 'Senior engineers embed in your operation, learn how it actually works, and ship reliable software into production. AI opportunities, legacy systems, and the bottlenecks stuck between business and engineering.',
      points: [
        'Senior builders embedded with your team, accountable end to end',
        'Legacy systems, AI features, and stalled prototypes taken to production',
        'The people who understand the problem are the people who build it',
      ],
      cta: 'See how we work',
    },
    benjamin: {
      label: 'Benjamin · customer operations',
      headline: 'The AI support platform we built.',
      body: 'Customer support and operational workflows across web chat, WhatsApp, and email. Grounded in your knowledge, with human escalation when judgment is needed.',
      points: [
        'Grounded in your knowledge base and your workflows',
        'Works across web chat, WhatsApp, and email',
        'Escalates to a human with full conversation context preserved',
      ],
      cta: 'Explore Benjamin',
      secondaryCta: 'Book a demo',
    },
    portfolio: {
      kicker: 'Services',
      title: 'From strategy to engineering.',
      body: 'Strategy executed, operations moving faster, software in production. Six lines of work to get you there.',
      cta: 'See the full catalogue',
      groups: [
        { id: 'strategy', accent: 19, name: 'Strategy', line: 'Hoshin Kanri from executive workshop to a plan your teams actually run.' },
        { id: 'workshops', accent: 10, name: 'Workshops', line: 'Value stream mapping, process redesign and Lean foundations, facilitated to a decision.' },
        { id: 'sdlc', accent: 1, name: 'Software delivery and AI', line: 'Software delivery rebuilt for the agent era under the Lean Pyramid 6.0.' },
        { id: 'discovery', accent: 10, name: 'Discovery', line: 'Legacy assessments and AI discovery that end in numbers, sequence and a probe.' },
        { id: 'delivery', accent: 1, name: 'FDE pods', line: 'One to three senior engineers directing agents, from problem to production.' },
        { id: 'recurring', accent: 19, name: 'Recurring', line: 'Executive advisory, board counsel and coaching that keep the system sharp.' },
      ],
    },
    method: {
      kicker: 'Maestro · the delivery operating system',
      title: 'Work you can watch, forecasts you can trust.',
      body: 'Our work runs on the Lean Pyramid 6.0 and stays visible in Maestro, the operating system we built for software delivery in the AI era: forecasts computed from measured throughput, decisions waiting on named people, agents whose work arrives with its evidence.',
      cta: 'Explore Maestro',
      shotAlt: 'Maestro metrics view with burn-up and Monte Carlo forecast, demo workspace with synthetic data',
    },
    firm: {
      kicker: 'The firm',
      title: 'A firm built like its own operating model',
      body: 'Small at the center, structured to scale: senior judgment where it matters, agent labor where it multiplies.',
      roles: [
        { name: 'Founder and Principal', line: 'Thirty years of software, twenty of Lean and transformation, on every engagement.' },
        { name: 'Advisory Board', line: 'Independent counsel around the firm. Being formed and announced name by name.', forming: true },
        { name: 'Senior Engineering Partners', line: 'The practitioners who staff pods, accountable end to end.' },
        { name: 'The agent workforce', line: 'Teams of agents under Maestro governance, doing the heavy lifting, their work verified by humans.' },
      ],
      cta: 'About the firm',
    },
    company: {
      eyebrow: 'The depth behind the work',
      headline: 'Built on a decade of real operational work.',
      body: 'Before Benjamin, Lean it 101 led transformation programs at Vale, Softplan, Ambev and other enterprise software companies. The practice was shaped inside the global Lean and Agile community, including five years on the Agile Alliance board of directors in the United States, working alongside the people who defined modern software delivery. That depth is what you hire when you bring us a problem, and what Benjamin is built on.',
      credentials: [
        { metric: 'US$9M+', label: 'in documented operational savings' },
        { metric: '80%', label: 'support tickets reduction at enterprise scale' },
        { metric: '30+', unit: 'Years', label: 'building software and operations' },
        { metric: '3,000+', label: 'people trained in Lean, Agile and engineering practices' },
      ],
    },
    track: {
      eyebrow: 'Track record',
      headline: 'Results that required real operational depth.',
      body: 'Programs that produced measurable operational results. They are the depth behind the work we do and the platform we built.',
      cta: 'See full case studies',
      items: [
        {
          client: 'Porter',
          metric: 'Lead to activation, redesigned',
          body: 'A three-day value stream redesign with 35+ executives and managers, covering the full flow from lead to customer activation. The new flow targets close to US$2M in cash efficiency, and we return at year end to measure it.',
        },
        {
          client: 'Vale',
          metric: 'US$9M+ annual savings',
          body: 'Root-cause analysis and operational redesign across contract commercialization, producing documented annual savings of more than R$50M.',
        },
        {
          client: 'Softplan',
          metric: '80% support tickets reduction',
          body: 'Workflow and support redesign that eliminated major operational waste and scaled the delivery system.',
        },
        {
          client: 'Segware',
          metric: 'Legacy rebuilt for scale',
          body: 'Rebuilt the Sigma Cloud legacy SaaS product into a modern platform prepared for scale, using concurrent engineering.',
        },
      ],
    },
  },

  'pt-BR': {
    seo: {
      title: 'Lean it 101 | Tornamos empresas boas em construir software',
      description:
        'Menos dinheiro queimado em software que nunca entra no ar. Estratégia com seus executivos, engenheiros seniores trabalhando com agentes de IA, e resultados chegando em produção semana a semana.',
    },
    hero: {
      eyebrow: 'Lean it 101 · Estratégia · Engenharia · IA',
      headline: 'Tornamos empresas',
      headlineAccent: 'boas em construir software.',
      body: 'Menos dinheiro queimado em software que nunca entra no ar. Reconstruímos como sua empresa planeja, constrói e entrega, e você vê os resultados chegarem em produção, semana a semana.',
      reassurance: 'Comece com um workflow. Respondemos em um dia útil.',
      speed: [
        'Diagnóstico em 1 a 2 semanas',
        'Primeira entrega em produção em 2 a 4 semanas',
      ],
      primaryCta: 'Veja o que fazemos',
      secondaryCta: 'Veja como trabalhamos',
      orbit: {
        aria: 'Sua operação no centro, com times seniores orbitando, dentro de um ciclo contínuo: descobrir, desenhar, construir, validar, entregar e transferir.',
        center: 'SUA OPERAÇÃO',
        items: ['Suporte · Faturamento', 'Onboarding · Dados', 'Handoffs · Relatórios'],
        pod: 'TIME',
        stages: ['Descobrir', 'Desenhar', 'Construir', 'Validar', 'Entregar', 'Transferir'],
        cap1: 'ENGENHEIROS SENIORES E AGENTES DE IA, DENTRO DA SUA OPERAÇÃO',
        cap2: 'DA DOR DO NEGÓCIO AO SOFTWARE RODANDO EM PRODUÇÃO',
      },
    },
    paths: {
      lead: 'Formas de trabalhar com a gente',
      connective:
        'Todos os caminhos rodam sobre o mesmo modelo operacional. A ajuda de transformação é opcional: chame a gente quando quiser a organização redesenhada em volta do sistema.',
      maestro: {
        label: 'Maestro · o sistema operacional da entrega',
        headline: 'Rode o sistema.',
        body: 'Adote o Maestro e rode a sua própria entrega nele: humanos e agentes de IA em um sistema só, previsões calculadas do seu próprio throughput, datas que você pode levar para a diretoria.',
        points: [
          'Visibilidade, controle e previsibilidade desde a primeira semana',
          'Funciona com os agentes de código que você já usa',
          'Sem exigência de consultoria',
        ],
        cta: 'Explorar o Maestro',
      },
    },
    build: {
      label: 'Forward Deployed Engineering',
      headline: 'Traga um workflow.',
      body: 'Engenheiros seniores entram na sua operação, entendem como ela funciona de verdade e colocam software confiável em produção. Oportunidades de IA, sistemas legados e os gargalos presos entre o negócio e a engenharia.',
      points: [
        'Builders seniores embarcados no seu time, responsáveis de ponta a ponta',
        'Sistemas legados, features de IA e protótipos parados levados à produção',
        'Quem entende o problema é quem constrói a solução',
      ],
      cta: 'Veja como trabalhamos',
    },
    benjamin: {
      label: 'Benjamin · operações com clientes',
      headline: 'A plataforma de IA para suporte que construímos.',
      body: 'Suporte ao cliente e workflows operacionais em chat web, WhatsApp e email. Fundamentado no seu conhecimento, com escalada para humanos quando precisa de critério.',
      points: [
        'Fundamentado na sua base de conhecimento e nos seus workflows',
        'Funciona em chat web, WhatsApp e email',
        'Escala para um humano com todo o contexto da conversa preservado',
      ],
      cta: 'Conhecer o Benjamin',
      secondaryCta: 'Agendar uma demo',
    },
    portfolio: {
      kicker: 'Serviços',
      title: 'Da estratégia à engenharia.',
      body: 'Estratégia executada, operação mais rápida, software em produção. Seis frentes de trabalho para chegar lá.',
      cta: 'Ver o catálogo completo',
      groups: [
        { id: 'strategy', accent: 19, name: 'Estratégia', line: 'Hoshin Kanri do workshop executivo a um plano que seus times de fato executam.' },
        { id: 'workshops', accent: 10, name: 'Workshops', line: 'Value stream mapping, redesenho de processos e fundamentos Lean, facilitados até a decisão.' },
        { id: 'sdlc', accent: 1, name: 'Entrega de software e IA', line: 'Desenvolvimento reconstruído para a era dos agentes sob a Pirâmide Lean 6.0.' },
        { id: 'discovery', accent: 10, name: 'Discovery', line: 'Assessments de legado e discovery de IA que terminam em números, sequência e uma sonda.' },
        { id: 'delivery', accent: 1, name: 'Pods FDE', line: 'Um a três engenheiros seniores dirigindo agentes, do problema à produção.' },
        { id: 'recurring', accent: 19, name: 'Recorrentes', line: 'Advisory executivo, conselho e coaching que mantêm o sistema afiado.' },
      ],
    },
    method: {
      kicker: 'Maestro · o sistema operacional da entrega',
      title: 'Trabalho que você acompanha, previsões em que você confia.',
      body: 'Nosso trabalho roda sobre a Pirâmide Lean 6.0 e permanece visível no Maestro, o sistema operacional que construímos para a entrega de software na era da IA: previsões calculadas do throughput medido, decisões esperando pessoas nomeadas, agentes cujo trabalho chega com sua evidência.',
      cta: 'Explorar o Maestro',
      shotAlt: 'Visão de métricas do Maestro com burn-up e previsão Monte Carlo, workspace de demonstração com dados sintéticos',
    },
    firm: {
      kicker: 'A firma',
      title: 'Uma firma construída como o próprio modelo operacional',
      body: 'Pequena no centro, estruturada para escalar: julgamento sênior onde importa, trabalho de agente onde multiplica.',
      roles: [
        { name: 'Fundador e Principal', line: 'Trinta anos de software, vinte de Lean e transformação, em cada engajamento.' },
        { name: 'Conselho Consultivo', line: 'Conselho independente ao redor da firma. Em formação, anunciado nome a nome.', forming: true },
        { name: 'Engenheiros Parceiros Seniores', line: 'Os praticantes que compõem os pods, responsáveis de ponta a ponta.' },
        { name: 'A força de agentes', line: 'Times de agentes sob governança do Maestro, fazendo o trabalho pesado, com o trabalho verificado por humanos.' },
      ],
      cta: 'Sobre a firma',
    },
    company: {
      eyebrow: 'A profundidade por trás do trabalho',
      headline: 'Construído sobre décadas de trabalho operacional real.',
      body: 'Antes das plataformas, a Lean it 101 liderou programas de transformação na Vale, Softplan, Ambev e outras empresas de software enterprise. A prática foi formada dentro da comunidade global de Lean e Agile, incluindo cinco anos no board of directors da Agile Alliance nos Estados Unidos, ao lado das pessoas que definiram a entrega de software moderna. Essa profundidade é o que você contrata quando traz um problema.',
      credentials: [
        { metric: 'R$50M+', label: 'em economia operacional documentada' },
        { metric: '80%', label: 'de redução de tickets de suporte em escala enterprise' },
        { metric: '30+', unit: 'Anos', label: 'construindo software e operações' },
        { metric: '3.000+', label: 'pessoas treinadas em Lean, Agile e práticas de engenharia' },
      ],
    },
    track: {
      eyebrow: 'Histórico',
      headline: 'Resultados que exigiram profundidade operacional real.',
      body: 'Programas que produziram resultados operacionais mensuráveis. São a profundidade por trás do trabalho que fazemos e das plataformas que construímos.',
      cta: 'Ver casos completos',
      items: [
        {
          client: 'Porter',
          metric: 'Do lead à ativação, redesenhado',
          body: 'Redesenho do fluxo de valor em três dias com mais de 35 executivos e gestores, cobrindo todo o fluxo do lead até a ativação do cliente. O novo fluxo mira perto de US$2M em eficiência de caixa, e voltamos no fim do ano para medir.',
        },
        {
          client: 'Vale',
          metric: 'R$50M de economia anual',
          body: 'Análise de causa raiz e redesenho operacional na comercialização de contratos, gerando economia anual documentada de mais de R$50M.',
        },
        {
          client: 'Softplan',
          metric: '80% de redução de tickets de suporte',
          body: 'Redesenho de workflow e de suporte que eliminou desperdício operacional relevante e escalou o sistema de entrega.',
        },
        {
          client: 'Segware',
          metric: 'Legado reconstruído para escala',
          body: 'Reconstrução do produto legado Sigma Cloud em uma plataforma moderna preparada para escala, com engenharia concorrente.',
        },
      ],
    },
  },

  'es': {
    seo: {
      title: 'Lean it 101 | Hacemos a las empresas buenas construyendo software',
      description:
        'Menos dinero quemado en software que nunca llega a producción. Estrategia con tus ejecutivos, ingenieros seniores trabajando con agentes de IA, y resultados llegando semana a semana.',
    },
    hero: {
      eyebrow: 'Lean it 101 · Estrategia · Ingeniería · IA',
      headline: 'Hacemos a las empresas',
      headlineAccent: 'buenas construyendo software.',
      body: 'Menos dinero quemado en software que nunca llega a producción. Reconstruimos cómo tu empresa planifica, construye y entrega, y ves los resultados llegar semana a semana.',
      reassurance: 'Empieza con un flujo de trabajo. Respondemos en un día hábil.',
      speed: [
        'Diagnóstico en 1 a 2 semanas',
        'Primera entrega en producción en 2 a 4 semanas',
      ],
      primaryCta: 'Mira lo que hacemos',
      secondaryCta: 'Ve cómo trabajamos',
      orbit: {
        aria: 'Tu operación en el centro, con equipos seniores orbitando, dentro de un ciclo continuo: descubrir, diseñar, construir, validar, entregar y transferir.',
        center: 'TU OPERACIÓN',
        items: ['Soporte · Facturación', 'Onboarding · Datos', 'Handoffs · Reportes'],
        pod: 'EQUIPO',
        stages: ['Descubrir', 'Diseñar', 'Construir', 'Validar', 'Entregar', 'Transferir'],
        cap1: 'INGENIEROS SENIORES Y AGENTES DE IA, DENTRO DE TU OPERACIÓN',
        cap2: 'DEL DOLOR DEL NEGOCIO AL SOFTWARE CORRIENDO EN PRODUCCIÓN',
      },
    },
    paths: {
      lead: 'Formas de trabajar con nosotros',
      connective:
        'Todos los caminos corren sobre el mismo modelo operativo. La ayuda de transformación es opcional: llámanos cuando quieras rediseñar la organización alrededor del sistema.',
      maestro: {
        label: 'Maestro · el sistema operativo de la entrega',
        headline: 'Corre el sistema.',
        body: 'Adopta Maestro y corre tu propia entrega en él: humanos y agentes de IA en un solo sistema, pronósticos calculados de tu propio throughput, fechas que puedes llevar al directorio.',
        points: [
          'Visibilidad, control y previsibilidad desde la primera semana',
          'Funciona con los agentes de código que ya usas',
          'Sin exigencia de consultoría',
        ],
        cta: 'Explorar Maestro',
      },
    },
    build: {
      label: 'Forward Deployed Engineering',
      headline: 'Trae un flujo de trabajo.',
      body: 'Ingenieros seniores se integran en tu operación, aprenden cómo funciona de verdad y llevan software confiable a producción. Oportunidades de IA, sistemas heredados y los cuellos de botella atascados entre el negocio y la ingeniería.',
      points: [
        'Builders senior integrados en tu equipo, responsables de principio a fin',
        'Sistemas heredados, features de IA y prototipos detenidos llevados a producción',
        'Quien entiende el problema es quien construye la solución',
      ],
      cta: 'Mira cómo trabajamos',
    },
    benjamin: {
      label: 'Benjamin · operaciones con clientes',
      headline: 'La plataforma de IA para soporte que construimos.',
      body: 'Soporte al cliente y flujos operativos en chat web, WhatsApp y email. Fundamentado en tu conocimiento, con escalada a humanos cuando se necesita criterio.',
      points: [
        'Fundamentado en tu base de conocimiento y tus flujos de trabajo',
        'Funciona en chat web, WhatsApp y email',
        'Escala a un humano con todo el contexto de la conversación preservado',
      ],
      cta: 'Explorar Benjamin',
      secondaryCta: 'Agendar una demo',
    },
    portfolio: {
      kicker: 'Servicios',
      title: 'De la estrategia a la ingeniería.',
      body: 'Estrategia ejecutada, operación más rápida, software en producción. Seis líneas de trabajo para llegar ahí.',
      cta: 'Ver el catálogo completo',
      groups: [
        { id: 'strategy', accent: 19, name: 'Estrategia', line: 'Hoshin Kanri del workshop ejecutivo a un plan que tus equipos realmente ejecutan.' },
        { id: 'workshops', accent: 10, name: 'Workshops', line: 'Value stream mapping, rediseño de procesos y fundamentos Lean, facilitados hasta la decisión.' },
        { id: 'sdlc', accent: 1, name: 'Entrega de software e IA', line: 'Desarrollo reconstruido para la era de los agentes bajo la Pirámide Lean 6.0.' },
        { id: 'discovery', accent: 10, name: 'Discovery', line: 'Assessments de legado y discovery de IA que terminan en números, secuencia y una sonda.' },
        { id: 'delivery', accent: 1, name: 'Pods FDE', line: 'De uno a tres ingenieros seniores dirigiendo agentes, del problema a producción.' },
        { id: 'recurring', accent: 19, name: 'Recurrentes', line: 'Advisory ejecutivo, consejo y coaching que mantienen el sistema afilado.' },
      ],
    },
    method: {
      kicker: 'Maestro · el sistema operativo de la entrega',
      title: 'Trabajo que puedes ver, pronósticos en los que puedes confiar.',
      body: 'Nuestro trabajo corre sobre la Pirámide Lean 6.0 y permanece visible en Maestro, el sistema operativo que construimos para la entrega de software en la era de la IA: pronósticos calculados del throughput medido, decisiones esperando a personas con nombre, agentes cuyo trabajo llega con su evidencia.',
      cta: 'Explorar Maestro',
      shotAlt: 'Vista de métricas de Maestro con burn-up y pronóstico Monte Carlo, workspace de demostración con datos sintéticos',
    },
    firm: {
      kicker: 'La firma',
      title: 'Una firma construida como su propio modelo operativo',
      body: 'Pequeña en el centro, estructurada para escalar: juicio senior donde importa, trabajo de agente donde multiplica.',
      roles: [
        { name: 'Fundador y Principal', line: 'Treinta años de software, veinte de Lean y transformación, en cada compromiso.' },
        { name: 'Consejo Asesor', line: 'Consejo independiente alrededor de la firma. En formación, anunciado nombre a nombre.', forming: true },
        { name: 'Ingenieros Socios Seniores', line: 'Los practicantes que integran los pods, responsables de punta a punta.' },
        { name: 'La fuerza de agentes', line: 'Equipos de agentes bajo gobernanza de Maestro, haciendo el trabajo pesado, con su trabajo verificado por humanos.' },
      ],
      cta: 'Sobre la firma',
    },
    company: {
      eyebrow: 'La profundidad detrás del trabajo',
      headline: 'Construido sobre décadas de trabajo operativo real.',
      body: 'Antes de las plataformas, Lean it 101 lideró programas de transformación en Vale, Softplan, Ambev y otras empresas de software enterprise. La práctica se formó dentro de la comunidad global de Lean y Agile, incluyendo cinco años en el board of directors de Agile Alliance en Estados Unidos, junto a las personas que definieron la entrega de software moderna. Esa profundidad es lo que contratas cuando nos traes un problema.',
      credentials: [
        { metric: 'US$9M+', label: 'en ahorros operativos documentados' },
        { metric: '80%', label: 'de reducción de tickets de soporte a escala enterprise' },
        { metric: '30+', unit: 'Años', label: 'construyendo software y operaciones' },
        { metric: '3.000+', label: 'personas formadas en Lean, Agile y prácticas de ingeniería' },
      ],
    },
    track: {
      eyebrow: 'Historial',
      headline: 'Resultados que requirieron profundidad operativa real.',
      body: 'Programas que produjeron resultados operativos medibles. Son la profundidad detrás del trabajo que hacemos y de las plataformas que construimos.',
      cta: 'Ver casos completos',
      items: [
        {
          client: 'Porter',
          metric: 'Del lead a la activación, rediseñado',
          body: 'Rediseño del flujo de valor en tres días con más de 35 ejecutivos y gerentes, cubriendo todo el flujo desde el lead hasta la activación del cliente. El nuevo flujo apunta a cerca de US$2M en eficiencia de caja, y volvemos a fin de año para medirlo.',
        },
        {
          client: 'Vale',
          metric: 'US$9M+ de ahorro anual',
          body: 'Análisis de causa raíz y rediseño operativo en la comercialización de contratos, generando ahorro anual documentado de más de R$50M.',
        },
        {
          client: 'Softplan',
          metric: '80% de reducción de tickets de soporte',
          body: 'Rediseño de flujo de trabajo y de soporte que eliminó desperdicios operativos y escaló el sistema de entrega.',
        },
        {
          client: 'Segware',
          metric: 'Legado reconstruído para escala',
          body: 'Reconstrucción del producto legado Sigma Cloud en una plataforma moderna preparada para escala, con ingeniería concurrente.',
        },
      ],
    },
  },
}

export const getHomeContent = (language) => {
  if (!language) return homeContent['en-US']
  const lower = language.toLowerCase()
  if (lower.startsWith('pt')) return homeContent['pt-BR']
  if (lower.startsWith('es')) return homeContent['es']
  return homeContent['en-US']
}
